import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { RUN_META, ruleTip } from "@/lib/format";
import { ruleMeaning } from "@/lib/ruleMeanings";
import { decisionReason, enterOffLine, nextScheduledRun, noBlockingRuleText, safetyBlockDetail, safetyBlockText } from "@/lib/decisionText";
import { QueryError, UpdatedAt, isNoSession, retryTransient } from "@/lib/queryState";
import { LIVE_REFETCH_MS, useLiveWindow } from "@/lib/marketHours";
import { currentDecision, decisionRuns, isLate, lateLabel } from "@/lib/checkpointState";
import { RULE_HINTS } from "@/components/SignalExplainer";
import { exitPlanOf, orderValidity } from "@/components/desk/DeskTicket";
import { TenAmRecordPanel } from "@/components/desk/TenAmRecord";
import {
  Badge, DataBanner, DataStateBadge, DecisionBadge, EmptyState, Icon, LogoMark, PageLoading, StatCell, dayLabel, fmt, normDecision,
  sessionDateLabel, timeET, timeSYD, useNow, usePageTitle, etAndLocal } from "@/sentinel";

const isEnter = (d) => d === "ENTER" || d === "ENTER PRE-OPEN";
const priceText = (v) => (v == null ? "—" : typeof v === "number" ? fmt(v) : String(v));

/**
 * /m?date=YYYY-MM-DD&run=n — the phone-first decision card linked from the per-run alert.
 * One card: the decision, run and time (ET, Sydney), the blocking rule in plain language or the
 * ENTER ticket with its exit plan and validity, data freshness, and a link to the full desk.
 */
export default function MobileDecision() {
  const { selectedDate, setSelectedDate, health } = useAuth();
  // Viewer-readable ENTER state from /health. Missing on an older backend: then say nothing.
  const enter = health?.enter;
  const enterLine = enterOffLine(enter);
  const [params] = useSearchParams();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") || "") ? params.get("date") : selectedDate;
  const wantRun = Number(params.get("run")) || null;
  const win = useLiveWindow(date);
  const now = useNow(5000);
  usePageTitle("Decision");

  // Opening the full desk from here shows the same session.
  useEffect(() => { if (date && date !== selectedDate) setSelectedDate(date); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionQ = useQuery({ queryKey: ["session", date], queryFn: () => api.getSession(date), enabled: !!date, retry: retryTransient });
  const cpsQ = useQuery({ queryKey: ["checkpoints", date], queryFn: () => api.getCheckpoints(date), enabled: !!date, retry: retryTransient, refetchInterval: win.polling ? LIVE_REFETCH_MS : false });
  const runs = decisionRuns(cpsQ.data || []);
  const current = currentDecision(cpsQ.data || []);
  const cp = runs.find((c) => c.run === wantRun) || current;
  const primaryRun = cp && cp.run >= 3 && cp.run <= 5 ? cp.run : null;
  const primaryQ = useQuery({ queryKey: ["primary", date, primaryRun], queryFn: () => api.getPrimary(date, primaryRun), enabled: !!date && !!primaryRun, retry: retryTransient });
  const candQ = useQuery({ queryKey: ["candidates", date, cp?.run], queryFn: () => api.getCandidates(date, cp.run), enabled: !!date && !!cp && !primaryRun, retry: retryTransient });

  const session = sessionQ.data;
  const decision = cp ? normDecision(cp.decision_label) : null;
  const primary = primaryQ.data?.checkpoint?.id === cp?.id ? primaryQ.data : null;
  const cand = primary?.candidate;
  const nearest = primary?.nearest_failed || (candQ.data?.candidates || []).filter((c) => !c.is_recalc).sort((a, b) => a.rank - b.rank)[0];
  const rule = cp?.blocking_rule || nearest?.blocking_rule;
  const plan = exitPlanOf(primary?.pick);
  const validity = orderValidity(plan, date, now);
  const at = cp?.evaluated_at || cp?.timestamp_et;
  const mode = win.live ? "LIVE" : win.isToday ? "SAVED" : "ARCHIVED";
  const deskLink = `/tonight${cp && cp.id !== current?.id ? `?run=${cp.run}` : ""}`;
  const viewingCurrent = !!cp && cp.id === current?.id;
  const killArmed = !!session?.kill_switch;
  // The same sentence the desk shows under the decision.
  const doneRuns = new Set((cpsQ.data || []).filter((c) => !c.recalc_of).map((c) => c.run));
  const nextRun = viewingCurrent ? nextScheduledRun(date, doneRuns, now, win.isToday) : null;
  const reason = cp ? decisionReason({ decision, checkpoint: cp, candidate: nearest, killArmed, killReason: session?.kill_reason, nextRun, enter, today: win.isToday }) : "";

  let body;
  if (!date || sessionQ.isLoading || cpsQ.isLoading) body = <PageLoading label="Loading the decision" lines={3} />;
  else if (sessionQ.isError && isNoSession(sessionQ.error)) body = <EmptyState icon="CalendarOff" title="No session recorded for this date" data-testid="m-no-session">{sessionDateLabel(date)}</EmptyState>;
  else if (cpsQ.isError && !cpsQ.data) body = <QueryError query={cpsQ} title="The decision could not load" what="the checkpoints" testId="m-error" />;
  else if (!cp) body = <EmptyState icon="GitCommitVertical" title="No decision yet" data-testid="m-empty">Run 1 is scheduled for {etAndLocal("08:40", { day: date })}.</EmptyState>;
  else body = (
    <div className="sn-stack" style={{ gap: "var(--space-4)" }}>
      <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
        <span className="sn-micro">Decision · Run {cp.run}{cp.id === current?.id ? " · current" : " · earlier run"}</span>
        <DecisionBadge decision={decision} size="standard" data-testid="m-decision" />
        {reason && <span style={{ fontWeight: 500, color: "var(--text-primary)" }} data-testid="m-reason">{reason}</span>}
        {enterLine && (
          <span className="sn-small sn-muted" data-testid="m-enter-off">
            <Icon name="CircleMinus" size={14} style={{ marginRight: 4, verticalAlign: "-2px" }} />{enterLine}
          </span>
        )}
        <span className="sn-small sn-muted" data-testid="m-time">
          <span className="sn-num">{timeET(at, { seconds: false })} ET</span> · <span className="sn-num">{dayLabel(at)} {timeSYD(at, { seconds: false })}</span> Sydney · cutoff <span className="sn-num">{cp.cutoff_et || RUN_META[cp.run]?.time} ET</span>
        </span>
        {isLate(cp) && <Badge icon="Clock" className="sn-prov" data-testid="m-late">LATE · {lateLabel(cp).toLowerCase()}</Badge>}
      </div>

      {isEnter(decision) && cand ? (
        <div className="sn-stack" style={{ gap: "var(--space-3)" }} data-testid="m-ticket">
          <span style={{ fontWeight: 600 }}>{cand.structure_name} · <span className="sn-num">{fmt(cand.short_strike, "strike")} / {fmt(cand.long_strike, "strike")}</span> · 0DTE</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "var(--space-3)" }}>
            <StatCell size="sm" label={cand.is_credit ? "Net credit" : "Net debit"} value={Math.abs(Number(cand.net_price) || 0)} kind="price" />
            <StatCell size="sm" label="Max entry" value={priceText(plan?.max_entry)} />
            {plan?.hold === "settlement" ? (
              <StatCell size="sm" label="Exit" value="Hold to settlement" />
            ) : (
              <>
                <StatCell size="sm" label="Profit target" value={priceText(plan?.profit_target)} />
                <StatCell size="sm" label="Stop" value={priceText(plan?.stop)} />
                <StatCell size="sm" label="Time exit" value={plan?.time_exit_et ? etAndLocal(plan.time_exit_et, { day: date }) : "—"} />
              </>
            )}
            <StatCell size="sm" label="Valid until" value={validity.label ? etAndLocal(validity.label, { day: date }) : "—"} />
          </div>
          {plan?.invalidation && <span className="sn-small sn-muted">Invalidation · <span style={{ color: "var(--text-primary)" }}>{plan.invalidation}</span></span>}
          {!validity.ok && <span className="sn-row sn-small" style={{ gap: "var(--space-2)" }} data-testid="m-expired"><Badge icon="Hourglass" className="sn-prov">Expired</Badge>{validity.reason}</span>}
        </div>
      ) : (
        <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid="m-block">
          <span className="sn-micro">{rule ? "Blocking rule" : "Why"}</span>
          <span style={{ fontWeight: 600 }}>
            {rule ? (ruleMeaning(rule) || ruleTip[rule] || `Rule ${rule}`) : noBlockingRuleText({ decision, checkpoint: cp, candidate: nearest })}
            {rule && <span className="sn-small sn-faint sn-num" style={{ fontWeight: 400 }}> {rule}</span>}
          </span>
          {rule && RULE_HINTS[rule] && <span className="sn-small sn-muted">{RULE_HINTS[rule]}</span>}
          {rule && ruleMeaning(rule) && ruleTip[rule] && <span className="sn-small sn-faint">Technical check: {ruleTip[rule]}.</span>}
          {(cp.safety_blocks || []).length > 0 && (
            <ul className="sn-stack" style={{ margin: "var(--space-1) 0 0", paddingLeft: 0, listStyle: "none", gap: "var(--space-1)" }} data-testid="m-safety-blocks">
              {cp.safety_blocks.map((b, i) => (
                <li key={`${b.code}-${i}`} className="sn-small sn-muted" style={{ overflowWrap: "anywhere" }}>
                  <span className="sn-micro" style={{ marginRight: 6 }}>Safety block</span>{safetyBlockText(b)}
                  <span className="sn-faint sn-num"> {b.code}</span>
                  {safetyBlockDetail(b) && <span className="sn-faint"> · {safetyBlockDetail(b)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-2)" }} data-testid="m-freshness">
        {mode === "LIVE" ? <DataStateBadge state="LIVE" /> : <Badge icon="Lock">{mode === "SAVED" ? "SAVED SESSION" : "ARCHIVED"}</Badge>}
        {session && session.provider !== "polygon" && <DataStateBadge state="STUB" />}
        <UpdatedAt at={cpsQ.dataUpdatedAt} prefix="Checked" />
      </div>
      <Link to={deskLink} className="sn-btn sn-btn-secondary sn-btn-block" style={{ minHeight: 44, textDecoration: "none" }} data-testid="m-open-desk">
        Open the full desk<Icon name="ArrowRight" />
      </Link>
    </div>
  );

  return (
    <div className="sn-root" style={{ minHeight: "100vh", background: "var(--surface-base)", padding: "var(--space-4)" }} data-testid="mobile-decision">
      <div className="sn-stack" style={{ maxWidth: 480, margin: "0 auto", gap: "var(--space-3)" }}>
        <div className="sn-row" style={{ gap: "var(--space-2)" }}>
          <LogoMark size={20} />
          <span style={{ fontWeight: 600 }}>Sentinel</span>
          <span className="sn-small sn-faint sn-sans-num" style={{ marginLeft: "auto" }}>{sessionDateLabel(date)}</span>
        </div>
        {session?.kill_switch && <DataBanner kind="kill-armed" inline>Every decision is forced to NO TRADE.</DataBanner>}
        {/* Roadmap Y5: the 10:00 am research record first; the old rule set's decision below it. */}
        <TenAmRecordPanel day={date} compact />
        <span className="sn-micro" data-testid="m-old-rules-head">Old rule set · kept for comparison (no edge found, ENTER off)</span>
        <section className="sn-panel" style={{ padding: "var(--space-4)" }} aria-label="Decision card">{body}</section>
        <p className="sn-small sn-faint" style={{ margin: 0 }}>Decision support only · Sentinel places no orders.</p>
      </div>
    </div>
  );
}
